import { Fragment } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { useMasterData } from '@ui-framework/index';
import PermissionButton from '@product-framework/components/PermissionButton';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import DistanceFromCustomer from './sections/DistanceFromCustomer';
import TitleSection from './sections/TitleSection';
import ServiceDispatchSettingInfo from './sections/ServiceDispatchSettingInfo';
import DispatchInfo from './sections/DispatchInfo';
import SkillsInfo from './sections/SkillsInfo';
import ContactSection from './sections/ContactSection';
import AssignedWithConditions from './sections/AssignedWithConditionsInfo';

function ServiceProviderInfo({
  serviceProvider,
  currentJob,
  setDialog,
  onClose,
}) {
  const { sp, location, index } = serviceProvider;

  const { pendingServiceJobStatus } = useMasterData();
  const assign = pendingServiceJobStatus.includes(currentJob?.serviceJobStatus);
  const activeServiceProvider = sp?.status === 'ACTIVE';

  const jobAssignedLocationId = currentJob?.serviceProviderLocation?.id;
  const differentDispatchLocation =
    jobAssignedLocationId != null &&
    location?.id != null &&
    String(jobAssignedLocationId) !== String(location.id);
  // Same SP only blocks when we know it is the same dispatch location; missing ids keep legacy disable.
  const assignToThisSpIsNoOp =
    sp?.id === currentJob?.serviceProviderId && !differentDispatchLocation;

  const isUnassigned = !currentJob?.serviceProviderId;
  const showReassign =
    !isUnassigned &&
    (sp?.id !== currentJob?.serviceProviderId || differentDispatchLocation);

  let skillRating = 0;
  if (location?.skills?.servicesBrand) {
    skillRating += 25;
  }
  if (location?.skills?.servicesIndustry) {
    skillRating += 25;
  }
  if (location?.skills?.withinTravelDistance) {
    skillRating += 25;
  }

  if (location?.skills?.servicesProductLine) {
    skillRating += 25;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '15px 10px 10px',
      }}>
      {onClose ? <DialogCloseButton onClose={onClose} /> : null}
      <Stack direction="column" spacing={1}>
        <TitleSection sp={sp} location={location} index={index} />
        <Divider />
        <DispatchInfo location={location} sp={sp} />
        <Divider />
        {sp?.dispatchEvent?.status === 'ACCEPTED_WITH_CONDITIONS' && (
          <AssignedWithConditions sp={sp} />
        )}
        <ServiceDispatchSettingInfo sp={sp} />
        <Divider />
        <SkillsInfo skillRating={skillRating} location={location} />
        <Divider />
        <DistanceFromCustomer
          jobLocation={{
            lat: currentJob?.locationLat,
            lng: currentJob?.locationLng,
          }}
          serviceProviderAddressLocation={{
            lat: location?.address?.lat,
            lng: location?.address?.lng,
          }}
          locationId={location?.id}
        />
        <Divider />
        <ContactSection location={location} />
        <Divider />
      </Stack>

      <Grid2
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1 }}>
        {activeServiceProvider ? (
          <PermissionButton
            permissionName="CAN_UPDATE_JOB_FROM_MAP"
            sx={{ textTransform: 'none' }}
            variant="outlined"
            size="small"
            onClick={() => setDialog('MESSAGE')}>
            <FormattedMessage id="SEND_MESSAGE" defaultMessage="Send Message" />
          </PermissionButton>
        ) : (
          <>&nbsp;</>
        )}
        <PermissionButton
          permissionName="CAN_UPDATE_JOB_FROM_MAP"
          size="small"
          variant="contained"
          disabled={assignToThisSpIsNoOp || !assign}
          onClick={() => setDialog('ASSIGN')}>
          {showReassign ? (
            <FormattedMessage id="BUTTON_REASSIGN" defaultMessage="Reassign" />
          ) : (
            <FormattedMessage id="BUTTON_ASSIGN" defaultMessage="Assign" />
          )}
        </PermissionButton>
      </Grid2>
    </Box>
  );
}

export default ServiceProviderInfo;
